import { NextPage, GetServerSideProps } from 'next';
import Head from 'next/head';
import { CaseDetailsLandingScreen } from '@/components/screens';
import { casesService } from '@/api';
import { CaseDetailI } from '@/interfaces';
import { HttpResponse } from '../../interfaces/httpClient';
import { normalizeError } from '@/lib';

interface Props {
	caseData: HttpResponse<CaseDetailI>;
}
const CasesDetailPage: NextPage<Props> = ({ caseData }) => {
	const baseUrl = process.env.NEXT_PUBLIC_SITE_URL;
	const fullUrl = `${baseUrl}/cases/${caseData.data.id}`;
	const origin = new URL(caseData.data.cover_image).origin;
	const raw = caseData.data.about_claim;
	const description = raw.replace(/<\/?(?:p|b|i)[^>]*>/g, '');

	return (
		<>
			<Head>
				<title>{caseData.data.title}</title>
				<link rel="preconnect" href={origin} crossOrigin="anonymous" />
				<link rel="preload" as="image" href={caseData.data.cover_image} />

				<meta property="og:title" content={caseData.data.title} />
				<meta property="og:description" content={description} />
				<meta property="og:image" content={caseData.data.cover_image} />
				<meta property="og:url" content={`${fullUrl}`} />
				<meta property="og:type" content="website" />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={caseData.data.title} />
				<meta name="twitter:description" content={description} />
				<meta name="twitter:image" content={caseData.data.cover_image} />
				<link rel="canonical" href={`${fullUrl}`} />
			</Head>
			<CaseDetailsLandingScreen caseData={caseData} />
		</>
	);
};

export const getServerSideProps: GetServerSideProps<Props> = async ({
	params,
	res,
}) => {
	try {
		const { slug } = params as { slug: string };
		if (!slug) {
			return { notFound: true };
		}

		const rawCase = await casesService.getById(slug);
		const caseApiData = rawCase?.data;
		if (!caseApiData?.id) return { notFound: true };

		const caseData: HttpResponse<CaseDetailI> = {
			statusCode: rawCase?.statusCode ?? 200,
			message: rawCase?.message ?? 'Generic Message',
			data: {
				id: caseApiData.id ?? '',
				title: caseApiData.title ?? '',
				about_claim: caseApiData.about_claim ?? '',
				cover_image: caseApiData.cover_image ?? '',
				eligibility_criteria: caseApiData.eligibility_criteria ?? '',
				applicants_target_goal: caseApiData.applicants_target_goal ?? '',
				advertising_spent: caseApiData.advertising_spent ?? 0,
				case_type: caseApiData.case_type ?? 'CLASS_ACTION',
				category_id: caseApiData.category_id ?? 0,
				category: caseApiData.category ?? 'Whistleblower',
				status_id: caseApiData.status_id ?? 0,
				status: caseApiData.status ?? 'Closed to new clients',
				about_company: caseApiData.about_company ?? '',
				registration_deadline: caseApiData.registration_deadline ?? '',
				entity_type: caseApiData.entity_type ?? '',
				thumbnail_image: caseApiData.thumbnail_image ?? '',
				created_at: caseApiData.created_at ?? undefined,
				updated_at: caseApiData.updated_at ?? undefined,
				states: caseApiData.states ?? [],
				principal_place_of_business:
					caseApiData.principal_place_of_business ?? {
						id: 0,
						name: '',
					},
				defendant_incorporation: caseApiData.defendant_incorporation ?? {
					id: 0,
					name: '',
				},
				brand: caseApiData.brand ?? {
					id: '',
					name: '',
					logo: '',
				},
				lawyers: caseApiData.lawyers ?? [],
			},
		};

		res.setHeader('Cache-Control', 'no-store');
		return {
			props: {
				caseData,
			},
		};
	} catch (error) {
		const err = normalizeError(error);
		console.error('Error details:', {
			message: err.message,
			error: error,
			stack: error instanceof Error ? error.stack : undefined,
			apiUrl: process.env.NEXT_PUBLIC_API,
			id: params?.id,
		});

		return {
			notFound: true,
		};
	}
};

export default CasesDetailPage;
